import React, { useRef } from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clientFetch } from "../apiConfig";
import WaveformPlayer from "../components/voice/WaveformPlayer";
import {
  Mic,
  Square,
  ArrowRight,
  LayoutDashboard,
  CheckCircle2,
  FileAudio2,
  Pause,
  Play,
} from "lucide-react";
import "./Train.css";
import { validateAudio } from "../utils/audioValidation";

const Train = () => {
  const [phrases, setPhrases] = useState(null);
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [isFinished, setIsFinished] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const navigate = useNavigate();
  const recordingStartTimeRef = useRef(null);

  const startRecording = async () => {
    // Clear any previous validation or mic errors before trying again
    setError(null);
    try {
      recordingStartTimeRef.current = Date.now();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone access error:", err); // eslint-disable-line no-console

      if (
        err.name === "NotAllowedError" ||
        err.name === "PermissionDeniedError"
      ) {
        setError(
          "Mic access denied. Please allow it in your browser settings to record.",
        );
      } else if (
        err.name === "NotFoundError" ||
        err.name === "DevicesNotFoundError"
      ) {
        setError("No microphone found. Please connect a recording device.");
      } else {
        setError(
          "Unable to access your microphone. Please check your system settings.",
        );
      }

      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    const startTime = recordingStartTimeRef.current;
    const duration = Date.now() - startTime;

    mediaRecorderRef.current.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });

      const { isValid, error: validationError } = await validateAudio(
        audioBlob,
        duration,
      );
      if (!isValid) {
        setError(validationError);
        setAudioURL(null);
        setBlob(null);
        return;
      }
      setError(null);
      setBlob(audioBlob);
      setAudioURL(URL.createObjectURL(audioBlob));

      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
    mediaRecorderRef.current.stop();
    setIsRecording(false);
  };

  const saveToCloudinary = async (audioBlob) => {
    const formData = new FormData();
    formData.append("file", audioBlob, "recording.wav");
    formData.append("phrase_id", phrases[phraseIndex]._id); // Send phrase ID to backend
    try {
      const response = await clientFetch("/api/upload-audio", {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      console.log("Upload response:", data); // eslint-disable-line no-console
      return data;
    } catch (error) {
      console.error("Upload error:", error); // eslint-disable-line no-console
      return { status: "error", message: error.message };
    }
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleNextAction = async () => {
    if (!blob) return;
    setIsSaving(true);

    const uploadResult = await saveToCloudinary(blob);

    setIsSaving(false);
    if (uploadResult.status === "success") {
      if (phraseIndex + 1 === phrases.length) {
        setIsFinished(true);
      } else {
        setPhraseIndex((prev) => prev + 1);
        setAudioURL(null);
        setBlob(null); // Reset for next phrase
      }
    } else {
      alert("Failed to save audio. Please try again.");
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await clientFetch("/api/auth/me");
        const data = await response.json();

        if (response.ok) {
          setUser(data.user);

          // If they are already fully trained, don't make them re-train
          // Send them directly forward to their voice profile.
          if (
            data.user &&
            typeof data.user.is_trained !== "undefined" &&
            data.user.is_trained === true
          ) {
            navigate("/voice-profile", { replace: true });
          }
        } else {
          navigate("/login");
        }
      } catch (error) {
        console.error("Failed to fetch user data", error); // eslint-disable-line no-console
        // Secure the fallback route in case of complete network/server failures
        navigate("/login");
      }
    };
    fetchUser();
  }, [navigate]);

  useEffect(() => {
    const fetchPhrases = async () => {
      try {
        const response = await clientFetch("/api/phrases");
        const data = await response.json();
        if (response.ok) {
          setPhrases(data.phrases);
        } else {
          console.error("Failed to fetch phrases", data.message); // eslint-disable-line no-console
        }
      } catch (error) {
        console.error("Failed to fetch phrases", error); // eslint-disable-line no-console
      }
    };
    fetchPhrases();
  }, []);

  if (!phrases) {
    return <div>Loading phrases...</div>;
  }

  return (
    <div className="train-container" aria-live="polite">
      {isFinished ? (
        <div className="success-message" role="alert">
          <div className="icon-celebration">
            <CheckCircle2
              size={77}
              strokeWidth={1.5}
              color="#8b5cf6"
              aria-hidden="true"
            />
          </div>
          <h2>Fantastic work, {user ? user.name : "there"}!</h2>
          <p>Training complete.</p>
          <p>All recordings saved successfully.</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="dashboard-button"
          >
            <LayoutDashboard size={18} aria-hidden="true" />
            <span>Back to Dashboard</span>
          </button>
        </div>
      ) : (
        <div className="training-interface">
          <h1>Training Script</h1>
          <p className="instructions">
            Record each sample sentence to calibrate your personalized voice
            profile.
          </p>
          <div className="phrase-box">
            <div className="phrase-status">
              <span>Step</span>
              <span
                className="current-step"
                aria-label={`Phrase ${phraseIndex + 1} of ${phrases.length}`}
              >
                {phraseIndex + 1} / {phrases.length}
              </span>
            </div>
            <p className="phrase-text">{phrases[phraseIndex].text}</p>
          </div>
          {audioURL && (
            <div className="audio-player-section">
              <div className="audio-info">
                <FileAudio2 size={16} color="#8b5cf6" aria-hidden="true" />
                <span> Review Recording {phraseIndex + 1}</span>
              </div>
              <div className="pill-player-container">
                <button
                  onClick={togglePlay}
                  className="pill-play-button"
                  aria-label={isPlaying ? "Pause recording" : "Play recording"}
                >
                  {isPlaying ? (
                    <Pause size={18} aria-hidden="true" fill="currentColor" />
                  ) : (
                    <Play
                      size={18}
                      aria-hidden="true"
                      fill="currentColor"
                      ml="0.125rem"
                    />
                  )}
                </button>
                <div className="pill-waveform-wrapper">
                  <WaveformPlayer
                    url={audioURL}
                    isPlaying={isPlaying}
                    onFinish={() => setIsPlaying(false)}
                  />
                </div>
              </div>
            </div>
          )}
          {error && (
            <p
              className="error-message"
              role="alert"
              style={{ color: "#cd0808" }}
            >
              {error}
            </p>
          )}
          <div className="controls-group">
            {!isRecording ? (
              <button
                className="record-button"
                onClick={startRecording}
                aria-label={
                  audioURL ? "Retry recording phrase" : "Start recording phrase"
                }
              >
                <Mic size={18} strokeWidth={2.5} aria-hidden="true" />
                {audioURL ? "Retry" : "Record"}
              </button>
            ) : (
              <button className="stop-button" onClick={stopRecording}>
                <Square size={18} fill="currentColor" aria-hidden="true" /> Stop
              </button>
            )}
            <button
              disabled={!audioURL || isRecording || isSaving}
              onClick={handleNextAction}
              className="next-button"
            >
              {isSaving ? (
                <span>Saving...</span>
              ) : phraseIndex === phrases.length - 1 ? (
                <span>
                  Complete <CheckCircle2 size={18} aria-hidden="true" />
                </span>
              ) : (
                <span>
                  Next <ArrowRight size={18} aria-hidden="true" />
                </span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Train;
